/**
 * API Endpoints Configuration - EduQ AI Admin Portal
 * 
 * Centralized configuration for all admin API endpoints
 * Adapted from main app api-endpoints.js
 * 
 * Last Updated: 2026-02-19 (Build 25 - Cache Bust)
 */

// ============================================================================
// ENVIRONMENT DETECTION
// ============================================================================

const getEnvironment = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'development';
    }
    if (hostname.includes('staging') || hostname.includes('amplifyapp')) {
      return 'staging';
    }
  }
  return import.meta.env?.MODE || 'production';
};

const getEnvVar = (key, fallback) => {
  const viteKey = `VITE_${key}`;
  if (typeof import.meta !== 'undefined' && import.meta.env?.[viteKey]) {
    return import.meta.env[viteKey];
  }
  return fallback;
};

// ============================================================================
// API ENDPOINTS CONFIGURATION
// ============================================================================

export const API_ENDPOINTS = {
  // -------------------------------------------------------------------------
  // ADMIN SERVICES (Core admin functionality)
  // -------------------------------------------------------------------------
  ADMIN: {
    /** Admin user management Lambda URL (CORS fixed - handler only, no Function URL CORS) */
    USER_MANAGEMENT: getEnvVar(
      'ADMIN_USER_MANAGEMENT_URL',
      'https://4omx3fjosx2krgf36lzqtdztmm0iwjan.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Admin analytics Lambda URL (CORS fixed - no duplicate headers) */
    ANALYTICS: getEnvVar(
      'ADMIN_ANALYTICS_URL',
      'https://iybfo2kd2kn5brlunyy3rkoxzi0cwplm.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Admin analytics handler Lambda URL - serves activity-trends, engagement-metrics, cohorts (CORS fixed) */
    ANALYTICS_HANDLER: getEnvVar(
      'ADMIN_ANALYTICS_HANDLER_URL',
      'https://tt6dxwxv7b45hvy3hcqrt4atrm0sbxku.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Admin audit logger Lambda URL (CORS fixed - no duplicate headers) */
    AUDIT_LOGGER: getEnvVar(
      'ADMIN_AUDIT_LOGGER_URL',
      'https://muq5xbqlm4kujaiurte2qwsbba0pnxys.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Admin system monitor Lambda URL (CORS fixed - URL updated from stale orphan) */
    SYSTEM_MONITOR: getEnvVar(
      'ADMIN_SYSTEM_MONITOR_URL',
      'https://hsfxi5i6pmm7eqwp737wmpufwa0rfano.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Admin promo code manager Lambda URL (CORS fixed - no duplicate headers) */
    PROMO_CODE: getEnvVar(
      'ADMIN_PROMO_CODE_URL',
      'https://zptwvc7e32hya5rh5zwxp7fn5e0iatrj.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Admin webhook manager Lambda URL (CORS fixed - no duplicate headers) */
    WEBHOOK_MANAGER: getEnvVar(
      'ADMIN_WEBHOOK_MANAGER_URL',
      'https://hbtkd6xpeclmlpjtlcazt3dgni0dwjrw.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Admin subscription management Lambda URL (CORS fixed - no duplicate headers) */
    SUBSCRIPTION: getEnvVar(
      'ADMIN_SUBSCRIPTION_URL',
      'https://r624d45455koaodxotzowjfpu40rfmbb.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Admin payment analytics Lambda URL (CORS fixed - no duplicate headers) */
    PAYMENT_ANALYTICS: getEnvVar(
      'ADMIN_PAYMENT_ANALYTICS_URL',
      'https://y6nro44e72fqkn6keed35me2vq0sxflw.lambda-url.ap-southeast-1.on.aws'
    ),
    
    // Admin portal Lambda functions (migrating to Direct Lambda deployment)
    /** Support tickets management - Direct Lambda (CORS fixed - no duplicate headers) */
    SUPPORT_TICKETS: getEnvVar(
      'ADMIN_SUPPORT_TICKETS_URL',
      'https://qpakmcwua2ymvqczsykpztyou40lkybe.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Feature flags management - Direct Lambda (CORS fixed - no duplicate headers) */
    FEATURE_FLAGS: getEnvVar(
      'ADMIN_FEATURE_FLAGS_URL',
      'https://trwdmjim3mcfyrijyly3fnqtfe0hcmlc.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Notification management - Direct Lambda (CORS fixed - no duplicate headers) */
    NOTIFICATIONS: getEnvVar(
      'ADMIN_NOTIFICATIONS_URL',
      'https://kpdfka2l2hc26ywqhri26wfcnm0luubk.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Content management (CORS fixed - no duplicate headers) */
    CONTENT_MANAGEMENT: getEnvVar(
      'ADMIN_CONTENT_MANAGEMENT_URL',
      'https://fzmadsvwhypwv3g4jg5322hiz40eqnah.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Data export - Direct Lambda (CORS fixed - no duplicate headers) */
    DATA_EXPORT: getEnvVar(
      'ADMIN_DATA_EXPORT_URL',
      'https://sywjpyxkbreznwzes3pcgbklka0jiigy.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Abuse detection - Direct Lambda (CORS fixed - no duplicate headers) */
    ABUSE_DETECTION: getEnvVar(
      'ADMIN_ABUSE_DETECTION_URL',
      'https://dso6muxeanmljmft74z6qn2f340dnaax.lambda-url.ap-southeast-1.on.aws'
    ),
    /** AI Configuration (model selector + API key management) - reads/writes AWS Secrets Manager */
    AI_CONFIG: getEnvVar(
      'ADMIN_AI_CONFIG_URL',
      'https://q73liawz5kskogsmyokmtrqrni0meydu.lambda-url.ap-southeast-1.on.aws'
    ),
    /** TTS Generator - Text-to-Speech using Amazon Polly */
    TTS_GENERATOR: getEnvVar(
      'ADMIN_TTS_GENERATOR_URL',
      '' // Set VITE_ADMIN_TTS_GENERATOR_URL after deploying admin-tts-generator Lambda
    ),
    /** Promo analytics query Lambda URL - server-side aggregation with 5-min cache */
    PROMO_ANALYTICS: getEnvVar(
      'ADMIN_PROMO_ANALYTICS_URL',
      '' // Set VITE_ADMIN_PROMO_ANALYTICS_URL after deploying promo-analytics-query Lambda
    ),
    /** Subscription end date service Lambda URL - set after deploying subscription-end-date-service */
    SUBSCRIPTION_END_DATE: getEnvVar(
      'ADMIN_SUBSCRIPTION_END_DATE_URL',
      'https://pkat3e7ch7p4x3rzdqyaline240yfnpj.lambda-url.ap-southeast-1.on.aws'
    ),
    /** Family upgrade handler Lambda URL - upgrade flow + admin upgrade history query */
    FAMILY_UPGRADE_HANDLER: getEnvVar(
      'FAMILY_UPGRADE_HANDLER_URL',
      'https://cmhtelnm7ukpvpgktm77ugowru0zocjr.lambda-url.ap-southeast-1.on.aws'
    ),
  },

  // -------------------------------------------------------------------------
  // CUSTOMER SUPPORT SERVICES (User data viewing)
  // -------------------------------------------------------------------------
  CHAT: {
    /** Chat history Lambda Function URL (migrated from API Gateway to fix CORS) */
    HISTORY: getEnvVar(
      'CHAT_HISTORY_URL',
      'https://ax7pzc2uwq2s2npjfgajkpyuhm0nlqvd.lambda-url.ap-southeast-1.on.aws'  // Function URL - CORS handled by Function URL config
    ),
  },

  USER: {
    /** User profile Lambda function URL */
    PROFILE: getEnvVar(
      'USER_PROFILE_URL',
      'https://vxrn5vge3kbdkuialmrsit5qua0gdcuv.lambda-url.ap-southeast-1.on.aws'
    ),
    /** User attributes manager Lambda URL */
    ATTRIBUTES: getEnvVar(
      'USER_ATTRIBUTES_URL',
      'https://5qzwbibbpuuadpidplmzutuk3a0gudec.lambda-url.ap-southeast-1.on.aws'
    ),
  },

  FILES: {
    /** File manager Lambda URL */
    FILE_MANAGER: getEnvVar(
      'FILE_MANAGER_URL',
      'https://my5oip7magpvtcryqvtofeqgje0psufk.lambda-url.ap-southeast-1.on.aws'
    ),
  },

  ANALYTICS: {
    /** Progress tracking Lambda URL */
    PROGRESS: getEnvVar(
      'PROGRESS_TRACKING_URL',
      'https://6rxhedlipnfjlknv6whfprvxhq0ecunq.lambda-url.ap-southeast-1.on.aws'
    ),
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getEndpoint = (path) => {
  const parts = path.split('.');
  let value = API_ENDPOINTS;
  for (const part of parts) {
    value = value?.[part];
  }
  return value;
};

export const CURRENT_ENVIRONMENT = getEnvironment();
export const AWS_REGION = getEnvVar('AWS_REGION', 'ap-southeast-1');

/**
 * Safely join a base API URL with a path, preventing double slashes.
 * @param {string} baseUrl - The base Function URL (e.g., from API_ENDPOINTS.ADMIN.*)
 * @param {string} path - The path to append (e.g., '/admin/analytics')
 * @returns {string} The joined URL with exactly one slash between base and path
 */
export const buildApiUrl = (baseUrl, path = '') => {
  const base = baseUrl.replace(/\/+$/, '');
  if (!path) return base;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${cleanPath}`;
};

export default API_ENDPOINTS;
